// T149-074 (MON-19 C): "ระบุผู้จ่าย" (identify payer): attach a customer,
// and optionally the invoice it paid, to a previously unidentified
// standalone payment (T149-070's unknown_payer / Payment.unidentified).
// Shared between PaymentListPage's row menu and PaymentDetailPage's header
// action so there is one picker, not two that could drift.
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Modal, SearchableDropdown } from '@/components/ui'
import { buildInvoiceOptionLabel } from '@/lib/invoiceOptionLabel'
import { useCustomerSearch } from '@/hooks/useCustomers'
import { useInvoices } from '@/hooks/useInvoices'
import { useIdentifyPayer } from '@/hooks/usePayments'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'
import type { Customer, Invoice } from '@/types'

// Mirrors QuickPayFormPage's invoice-picker sentinel: "pick no invoice, name
// only the payer." Not '', which SearchableDropdown treats as unselected.
const NO_INVOICE_VALUE = 'none'

interface IdentifyPayerModalProps {
  paymentId: number
  isOpen: boolean
  onClose: () => void
}

export function IdentifyPayerModal({ paymentId, isOpen, onClose }: IdentifyPayerModalProps) {
  const { t } = useTranslation('payments')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('payments')
  const identifyPayer = useIdentifyPayer()

  const [customerQuery, setCustomerQuery] = useState('')
  const [customerPage, setCustomerPage] = useState(1)
  const [accumulatedCustomers, setAccumulatedCustomers] = useState<Customer[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState('')

  const [invoiceQuery, setInvoiceQuery] = useState('')
  const [invoicePage, setInvoicePage] = useState(1)
  const [accumulatedInvoices, setAccumulatedInvoices] = useState<Invoice[]>([])
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(NO_INVOICE_VALUE)
  // The picked invoice, kept independently of the current search results
  // so its option (and label) survive a later search that no longer returns
  // it. Cleared only when the customer changes (selectedInvoiceId itself is
  // the source of truth for what gets submitted; this is display-only).
  const [pinnedInvoice, setPinnedInvoice] = useState<Invoice | null>(null)

  // Reset all picker state each time the modal (re)opens, otherwise a
  // second use on a different row would carry over the first row's choice.
  useEffect(() => {
    if (!isOpen) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: fresh pickers on every open, mirrors VerifyPaymentModal's reason reset
    setCustomerQuery('')
    setCustomerPage(1)
    setAccumulatedCustomers([])
    setSelectedCustomerId('')
    setInvoiceQuery('')
    setInvoicePage(1)
    setAccumulatedInvoices([])
    setSelectedInvoiceId(NO_INVOICE_VALUE)
    setPinnedInvoice(null)
  }, [isOpen])

  const { data: customerSearchData, isFetching: isCustomerLoading } = useCustomerSearch(customerQuery, {
    fetchOnEmpty: isOpen,
    page: customerPage,
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: a new search query invalidates the accumulated page list, mirrors QuickPayFormPage's own customer-search paging
    setCustomerPage(1)
    setAccumulatedCustomers([])
  }, [customerQuery])

  useEffect(() => {
    if (!customerSearchData?.data) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: accumulates load-more pages onto local state, same shape as QuickPayFormPage's customer picker
    setAccumulatedCustomers((prev) => {
      if (customerPage === 1) return customerSearchData.data
      const existingIds = new Set(prev.map((c) => c.id))
      return [...prev, ...customerSearchData.data.filter((c) => !existingIds.has(c.id))]
    })
  }, [customerSearchData, customerPage])

  const customerOptions = accumulatedCustomers.map((c) => ({
    value: String(c.id),
    label: c.name + (c.phone ? ` · ${c.phone}` : ''),
  }))
  const hasMoreCustomers = !!customerSearchData?.meta && customerSearchData.meta.current_page < customerSearchData.meta.total_pages
  const selectedCustomerIdNum = selectedCustomerId ? Number(selectedCustomerId) : undefined

  // Invoice picker narrows to the chosen customer's own unpaid invoices,
  // same as QuickPayFormPage's ?customer_id= narrowing and disabled until a
  // customer is picked (an invoice cannot outlive its own required field).
  const { data: invoicesData, isFetching: isInvoiceLoading } = useInvoices(
    invoicePage,
    'unpaid',
    undefined,
    undefined,
    undefined,
    selectedCustomerIdNum,
    invoiceQuery || undefined,
    undefined,
    { allPeriods: true },
  )

  // Only a changed CUSTOMER invalidates the invoice already picked (the
  // invoice-customer-mismatch 422 would just bounce it straight back). A
  // changed search query never clears it: SearchableDropdown calls
  // onSearch('') every time the picker is reopened, and a debounced search
  // can land after a pick, so tying the pick to the query wiped it and
  // identify_payer went out with the customer only.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: a new customer invalidates the accumulated page list and any invoice already picked under the previous customer
    setInvoicePage(1)
    setAccumulatedInvoices([])
    setSelectedInvoiceId(NO_INVOICE_VALUE)
    setPinnedInvoice(null)
  }, [selectedCustomerId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: a new search query invalidates only the accumulated page list, never the chosen invoice
    setInvoicePage(1)
    setAccumulatedInvoices([])
  }, [invoiceQuery])

  useEffect(() => {
    if (!invoicesData?.data) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: accumulates load-more pages onto local state, same shape as QuickPayFormPage's invoice picker
    setAccumulatedInvoices((prev) => {
      if (invoicePage === 1) return invoicesData.data
      const existingIds = new Set(prev.map((inv) => inv.id))
      return [...prev, ...invoicesData.data.filter((inv) => !existingIds.has(inv.id))]
    })
  }, [invoicesData, invoicePage])

  // The pinned invoice's option is kept in the list even once a later
  // search's results no longer include it, so SearchableDropdown's own
  // `options.find(o => o.value === value)` still resolves a label instead of
  // falling back to the placeholder. Deduped against the live results in
  // case the same invoice is already present there.
  const invoiceOptions = [
    { value: NO_INVOICE_VALUE, label: t('form.no_invoice_option') },
    ...(pinnedInvoice && !accumulatedInvoices.some((inv) => inv.id === pinnedInvoice.id)
      ? [{ value: String(pinnedInvoice.id), label: buildInvoiceOptionLabel(pinnedInvoice) }]
      : []),
    ...accumulatedInvoices.map((inv) => ({ value: String(inv.id), label: buildInvoiceOptionLabel(inv) })),
  ]
  const hasMoreInvoices = !!invoicesData?.meta && invoicesData.meta.current_page < invoicesData.meta.total_pages
  const selectedInvoiceIdNum =
    selectedInvoiceId && selectedInvoiceId !== NO_INVOICE_VALUE ? Number(selectedInvoiceId) : undefined

  const handleInvoiceChange = (value: string) => {
    setSelectedInvoiceId(value)
    if (value === NO_INVOICE_VALUE) {
      setPinnedInvoice(null)
      return
    }
    const picked = accumulatedInvoices.find((inv) => String(inv.id) === value) ?? pinnedInvoice
    setPinnedInvoice(picked ?? null)
  }

  const close = () => {
    onClose()
  }

  const handleSubmit = async () => {
    if (!selectedCustomerIdNum) return
    try {
      await identifyPayer.mutateAsync({
        id: paymentId,
        customer_id: selectedCustomerIdNum,
        invoice_id: selectedInvoiceIdNum,
      })
      addToast({ type: 'success', message: t('identify_payer.success') })
      close()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('identify_payer.title')}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {tc('actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} loading={identifyPayer.isPending} disabled={!selectedCustomerIdNum}>
            {t('identify_payer.submit')}
          </Button>
        </>
      }
    >
      {/*
        With only two short fields the modal is shorter than an open
        SearchableDropdown list (max-h-80), so the list ran past the
        modal's box and was clipped. The min-height leaves room for one open
        list below either trigger.
      */}
      <div className="space-y-4 min-h-[420px]">
        <p className="text-sm text-base-content/70">{t('identify_payer.hint')}</p>
        <SearchableDropdown
          label={t('identify_payer.customer_label')}
          placeholder={t('quick_pay.customer_placeholder')}
          value={selectedCustomerId}
          options={customerOptions}
          onChange={setSelectedCustomerId}
          onSearch={setCustomerQuery}
          isLoading={isCustomerLoading}
          onLoadMore={() => setCustomerPage((p) => p + 1)}
          hasMore={hasMoreCustomers}
          required
        />
        <SearchableDropdown
          label={t('identify_payer.invoice_label')}
          placeholder={t('form.invoice_search_placeholder')}
          value={selectedInvoiceId}
          options={invoiceOptions}
          onChange={handleInvoiceChange}
          onSearch={setInvoiceQuery}
          isLoading={isInvoiceLoading}
          onLoadMore={() => setInvoicePage((p) => p + 1)}
          hasMore={hasMoreInvoices}
          disabled={!selectedCustomerIdNum}
        />
      </div>
    </Modal>
  )
}
